import { useState } from 'react';
import { useAdminData } from '../context/AdminDataContext';
import {
  Search, Eye, Trash2, ChevronLeft, ChevronRight,
  MessageSquare, Download, Clock, Mail, Phone,
  CheckCircle, AlertCircle, XCircle, RefreshCw, X
} from 'lucide-react';
import { toast } from 'sonner';

const ITEMS_PER_PAGE = 8;

const STATUS_CONFIG = {
  New: { color: 'text-blue-600 bg-blue-50 border-blue-200', icon: AlertCircle },
  Contacted: { color: 'text-amber-600 bg-amber-50 border-amber-200', icon: RefreshCw },
  'In Progress': { color: 'text-purple-600 bg-purple-50 border-purple-200', icon: Clock },
  Closed: { color: 'text-green-600 bg-green-50 border-green-200', icon: CheckCircle },
};

const STATUS_CARD = {
  New: { bg: 'bg-blue-50 border-blue-200', icon: 'text-blue-500', count: 'text-blue-700' },
  Contacted: { bg: 'bg-amber-50 border-amber-200', icon: 'text-amber-500', count: 'text-amber-700' },
  'In Progress': { bg: 'bg-purple-50 border-purple-200', icon: 'text-purple-500', count: 'text-purple-700' },
  Closed: { bg: 'bg-green-50 border-green-200', icon: 'text-green-500', count: 'text-green-700' },
};

function EnquiryDetailModal({ enquiry, onClose, onStatusChange }) {
  return (
    <div className="fixed inset-0 bg-black/25 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white border border-gray-200 rounded-2xl w-full max-w-lg shadow-2xl">
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
          <h3 className="text-slate-700 font-semibold font-outfit">Enquiry Details</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-5">
          <div className="flex items-center gap-3">
            <span className={`px-3 py-1 rounded-lg text-xs font-manrope font-semibold border ${STATUS_CONFIG[enquiry.status]?.color}`}>{enquiry.status}</span>
            <span className="text-slate-400 text-xs font-manrope">{new Date(enquiry.created_at || enquiry.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-slate-400 text-xs font-manrope mb-1">Name</p>
              <p className="text-slate-700 text-sm font-manrope font-semibold">{enquiry.name}</p>
            </div>
            <div>
              <p className="text-slate-400 text-xs font-manrope mb-1">Project Interest</p>
              <p className="text-slate-700 text-sm font-manrope font-medium">{enquiry.project || '—'}</p>
            </div>
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-slate-400" />
              <a href={`mailto:${enquiry.email}`} className="text-[#47ace2] text-sm font-manrope hover:underline">{enquiry.email}</a>
            </div>
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-slate-400" />
              <a href={`tel:${enquiry.phone}`} className="text-[#47ace2] text-sm font-manrope hover:underline">{enquiry.phone}</a>
            </div>
          </div>
          <div>
            <p className="text-slate-400 text-xs font-manrope mb-2">Message</p>
            <p className="text-slate-600 text-sm font-manrope leading-relaxed bg-gray-50 rounded-xl p-4 border border-gray-100">{enquiry.message}</p>
          </div>
          <div>
            <p className="text-slate-400 text-xs font-manrope mb-2">Source</p>
            <span className="px-3 py-1 bg-gray-100 text-slate-500 text-xs rounded-lg font-manrope">{enquiry.source}</span>
          </div>
          <div>
            <p className="text-slate-500 text-xs font-manrope mb-2 font-medium">Update Lead Status</p>
            <div className="flex flex-wrap gap-2">
              {Object.keys(STATUS_CONFIG).map(status => (
                <button
                  key={status}
                  onClick={async () => {
                    try {
                      await onStatusChange(enquiry.id, status);
                      onClose();
                      toast.success(`Status updated to ${status}`);
                    } catch (err) {
                      toast.error(err.message || 'Failed to update status');
                    }
                  }}
                  className={`px-3 py-1.5 rounded-xl text-xs font-manrope font-medium border transition-all ${enquiry.status === status ? STATUS_CONFIG[status].color : 'border-gray-200 text-slate-500 hover:border-gray-300 bg-white'}`}
                >
                  {status}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function EnquiriesPage() {
  const { enquiries, updateEnquiryStatus, deleteEnquiry } = useAdminData();
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSource, setFilterSource] = useState('all');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);

  const sources = ['all', ...new Set(enquiries.map(e => e.source))];

  const filtered = enquiries.filter(e => {
    const matchSearch = !search || e.name.toLowerCase().includes(search.toLowerCase()) || e.email.toLowerCase().includes(search.toLowerCase()) || e.project?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === 'all' || e.status === filterStatus;
    const matchSource = filterSource === 'all' || e.source === filterSource;
    return matchSearch && matchStatus && matchSource;
  });

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const paginated = filtered.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);

  const handleDelete = async (id) => {
    if (window.confirm('Delete this enquiry?')) {
      try {
        await deleteEnquiry(id);
        toast.success('Enquiry deleted');
      } catch (err) {
        toast.error(err.message || 'Failed to delete');
      }
    }
  };

  const handleExport = () => {
    const csv = [
      ['Name', 'Email', 'Phone', 'Project', 'Status', 'Source', 'Date', 'Message'],
      ...filtered.map(e => [e.name, e.email, e.phone, e.project, e.status, e.source, new Date(e.created_at || e.createdAt).toLocaleDateString(), `"${e.message}"`]),
    ].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'enquiries.csv'; a.click();
    toast.success('Exported to CSV');
  };

  return (
    <div className="p-6 md:p-8 space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 font-outfit">Enquiries</h1>
          <p className="text-slate-400 font-manrope text-sm mt-1">{enquiries.length} total · {enquiries.filter(e => e.status === 'New').length} new</p>
        </div>
        <button onClick={handleExport} className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 hover:bg-gray-50 text-slate-600 rounded-xl font-manrope text-sm font-medium transition-colors shadow-sm">
          <Download className="w-4 h-4" />
          Export CSV
        </button>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {Object.entries(STATUS_CONFIG).map(([status, { icon: Icon }]) => {
          const count = enquiries.filter(e => e.status === status).length;
          const cfg = STATUS_CARD[status];
          return (
            <button key={status}
              onClick={() => { setFilterStatus(filterStatus === status ? 'all' : status); setPage(1); }}
              className={`bg-white border-2 rounded-xl p-4 text-left transition-all hover:shadow-md ${filterStatus === status ? cfg.bg + ' ' + cfg.bg.split(' ')[1] : 'border-gray-200 hover:border-gray-300'}`}
            >
              <Icon className={`w-4 h-4 mb-2 ${filterStatus === status ? cfg.icon : 'text-slate-400'}`} />
              <div className={`text-2xl font-bold font-outfit ${filterStatus === status ? cfg.count : 'text-slate-700'}`}>{count}</div>
              <div className="text-slate-400 text-xs font-manrope">{status}</div>
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-2xl p-4 flex flex-wrap gap-3 items-center shadow-sm">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input type="text" placeholder="Search by name, email, project..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-xl text-slate-700 text-sm font-manrope placeholder-slate-400 focus:outline-none focus:border-[#47ace2]" />
        </div>
        <select value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(1); }} className="bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-slate-600 text-sm font-manrope focus:outline-none">
          <option value="all">All Status</option>
          {Object.keys(STATUS_CONFIG).map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={filterSource} onChange={e => { setFilterSource(e.target.value); setPage(1); }} className="bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-slate-600 text-sm font-manrope focus:outline-none">
          {sources.map(s => <option key={s} value={s}>{s === 'all' ? 'All Sources' : s}</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/60">
                <th className="text-left px-5 py-4 text-slate-400 text-xs font-manrope font-semibold uppercase tracking-wide">Name</th>
                <th className="text-left px-5 py-4 text-slate-400 text-xs font-manrope font-semibold uppercase tracking-wide hidden md:table-cell">Project</th>
                <th className="text-left px-5 py-4 text-slate-400 text-xs font-manrope font-semibold uppercase tracking-wide hidden lg:table-cell">Source</th>
                <th className="text-left px-5 py-4 text-slate-400 text-xs font-manrope font-semibold uppercase tracking-wide hidden lg:table-cell">Date</th>
                <th className="text-left px-5 py-4 text-slate-400 text-xs font-manrope font-semibold uppercase tracking-wide">Status</th>
                <th className="px-5 py-4"></th>
              </tr>
            </thead>
            <tbody>
              {paginated.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-16 text-slate-400 font-manrope">
                  <MessageSquare className="w-10 h-10 mx-auto mb-3 opacity-25" /><p>No enquiries found</p>
                </td></tr>
              ) : paginated.map(e => (
                <tr key={e.id} className="border-b border-gray-50 hover:bg-gray-50/60 transition-colors">
                  <td className="px-5 py-4">
                    <p className="text-slate-700 text-sm font-manrope font-semibold">{e.name}</p>
                    <p className="text-slate-400 text-xs font-manrope">{e.email}</p>
                  </td>
                  <td className="px-5 py-4 hidden md:table-cell"><p className="text-slate-500 text-sm font-manrope">{e.project || '—'}</p></td>
                  <td className="px-5 py-4 hidden lg:table-cell">
                    <span className="px-2 py-1 bg-gray-100 text-slate-500 text-xs rounded-lg font-manrope">{e.source}</span>
                  </td>
                  <td className="px-5 py-4 hidden lg:table-cell">
                    <p className="text-slate-400 text-xs font-manrope">{new Date(e.created_at || e.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</p>
                  </td>
                  <td className="px-5 py-4">
                    <span className={`px-2.5 py-1 rounded-lg text-xs font-manrope font-semibold border ${STATUS_CONFIG[e.status]?.color}`}>{e.status}</span>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-2 justify-end">
                      <button onClick={() => setSelected(e)} className="p-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-slate-400 hover:text-slate-600 transition-colors"><Eye className="w-4 h-4" /></button>
                      <button onClick={() => handleDelete(e.id)} className="p-1.5 rounded-lg bg-red-50 hover:bg-red-100 text-red-400 hover:text-red-500 transition-colors"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-2 rounded-xl bg-white border border-gray-200 text-slate-400 hover:text-slate-600 disabled:opacity-40"><ChevronLeft className="w-4 h-4" /></button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
            <button key={p} onClick={() => setPage(p)} className={`w-9 h-9 rounded-xl text-sm font-manrope transition-colors ${p === page ? 'bg-[#47ace2] text-white shadow-sm' : 'bg-white border border-gray-200 text-slate-500 hover:text-slate-700'}`}>{p}</button>
          ))}
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="p-2 rounded-xl bg-white border border-gray-200 text-slate-400 hover:text-slate-600 disabled:opacity-40"><ChevronRight className="w-4 h-4" /></button>
        </div>
      )}

      {selected && (
        <EnquiryDetailModal
          enquiry={selected}
          onClose={() => setSelected(null)}
          onStatusChange={(id, status) => { updateEnquiryStatus(id, status); setSelected(prev => ({ ...prev, status })); }}
        />
      )}
    </div>
  );
}
